import classNames from "classnames";
import * as React from "react";
import { CalendarScheduleStyle } from "./calendar-schedule-style";
import { formatShortTime } from "common/components/localization";
import sortBy from "lodash/sortBy";
import flatten from "lodash/flatten";
import { useEventListener } from "hooks/use-event-listener";
import { getNowTimeZoneAgnostic } from "common/Support";

import HorizontalCalendar from "./private/horizontal-calendar";
import kebabCase from "lodash/kebabCase";

export interface CalendarEvent<T> extends Event {
    readonly data: T;
}

export interface Event {
    readonly name: string;
    readonly description: string | undefined;
    readonly start: Date;
    readonly end: Date;
    readonly itemID: string | undefined;
}

interface SelectionEvent extends Event {
    readonly selected?: boolean;
    readonly key: number;
    readonly original: any;
}

interface ChunkedEvent extends Event {
    readonly numColumns: number;
    readonly col: number;
    readonly selected: boolean;
    readonly key: number;
    readonly original: any;
}

export interface CalendarScheduleProps<T> {
    readonly className?: string;
    readonly header: string;
    readonly viewDate: Date;
    readonly events: readonly CalendarEvent<T>[];
    readonly selection?: Event;

    readonly onDateClick?: (date: Date) => void;
    readonly onHeaderClick?: () => void;
    readonly onSelectionChange?: (event: Event) => void;
    readonly onEventClick?: (event: CalendarEvent<T>) => void;
}

function eventIsDuringDay(event: Event, day: Date): boolean {
    if (
        new Date(event.start).setUTCHours(0, 0, 0, 0) === new Date(day).setUTCHours(0, 0, 0, 0) ||
        new Date(event.end).setUTCHours(0, 0, 0, 0) === new Date(day).setUTCHours(0, 0, 0, 0)
    ) {
        return true;
    }

    return event.start < day && event.end > day;
}

function getEventStyle(current: Date, startDate: Date, endDate: Date, overlaps: number, col: number) {
    // const startMinutes = Math.floor((startDate.getUTCHours() * 60 + startDate.getUTCMinutes()) / 15) * 15;
    // const endMinutes = Math.ceil((endDate.getUTCHours() * 60 + endDate.getUTCMinutes()) / 15) * 15;
    const startMinutes = Math.floor(startDate.getUTCHours() * 60 + startDate.getUTCMinutes());

    const endMinutes = Math.ceil(endDate.getUTCHours() * 60 + endDate.getUTCMinutes());

    const midnight = new Date(current);
    midnight.setUTCDate(midnight.getUTCDate() + 1);
    midnight.setUTCHours(0, 0, 0, 0);
    const start = startDate < current ? 0 : startMinutes;
    const end = endDate >= midnight ? 1440 : endMinutes;

    const width = 100 / (overlaps + 1);
    const x = width * col;

    return {
        top: start / 1.2,
        height: (end - start) / 1.2,
        width: `${width}%`,
        left: `${x}%`,
    };
}

function chunkAndArrangeEvents(events: SelectionEvent[]): ChunkedEvent[] {
    const chunks: SelectionEvent[][] = [];

    let end = new Date(0, 0, 0);
    let current: SelectionEvent[] = [];
    events.forEach(e => {
        if (e.start >= end) {
            if (current.length > 0) {
                chunks.push(current);
            }
            current = [e];
            end = e.end;
        } else {
            current.push(e);
            if (e.end > end) {
                end = e.end;
            }
        }
    });
    if (current.length > 0) {
        chunks.push(current);
    }

    const chunkedChunks = chunks.map(c => {
        let toEmpty = [...c];
        let col = 0;
        let swapCol = -1;
        let toFill: ChunkedEvent[] = [];
        let rejected: SelectionEvent[] = [];
        while (toEmpty.length > 0) {
            let chunkEnd = new Date(0, 0, 0);
            for (const e of toEmpty) {
                if (e.start >= chunkEnd) {
                    chunkEnd = e.end;
                    toFill.push({
                        ...e,
                        col,
                        numColumns: -1,
                        selected: e.selected === true,
                        original: e.original,
                    });
                    if (e.selected === true) {
                        swapCol = col;
                    }
                } else {
                    rejected.push(e);
                }
            }
            toEmpty = rejected;
            rejected = [];
            col++;
        }

        if (swapCol > 0) {
            toFill = toFill.map(ce => ({
                ...ce,
                col: ce.col === 0 ? swapCol : ce.col === swapCol ? 0 : ce.col,
            }));
        }

        return toFill.map(ce => ({
            ...ce,
            numColumns: col - 1,
        }));
    });

    return flatten(chunkedChunks);
}

function isWithin15Minutes(event: Event | undefined, date: Date) {
    if (event === undefined) return;

    return (
        Math.abs(event.start.getTime() - date.getTime()) <= 9e5 || Math.abs(event.end.getTime() - date.getTime()) <= 9e5
    );
}

function CalendarSchedule<T>(p: React.PropsWithChildren<CalendarScheduleProps<T>>) {
    const {
        className,
        viewDate: viewDateReal,
        header,
        events,
        selection,
        onDateClick,
        onHeaderClick,
        onSelectionChange,
        onEventClick,
    } = p;

    const [dragging, setDragging] = React.useState<string>();
    const [dragStart, setDragStart] = React.useState<[number, number]>();
    const [draggingSelection, setDraggingSelection] = React.useState<Event>();
    const elRef = React.useRef<HTMLDivElement | null>(null);
    const scrollerRef = React.useRef<HTMLDivElement | null>(null);

    const viewDate = React.useMemo(() => {
        const d = new Date(viewDateReal);
        d.setUTCHours(0, 0, 0, 0);
        return d;
    }, [viewDateReal]);

    const withSelections: SelectionEvent[] = [
        ...events.map((e, ei) => {
            let end = e.end;
            if (e.start.getTime() === end.getTime()) {
                end = new Date(end);
                end.setHours(end.getHours() + 1);
            }

            return { ...e, key: ei, original: e, end };
        }),
        ...(selection === undefined || !eventIsDuringDay(selection, viewDate)
            ? []
            : [
                  {
                      ...selection,
                      selected: true,
                      key: -1,
                      original: undefined,
                  },
              ]),
    ];

    const ordered = sortBy(
        withSelections.filter(e => eventIsDuringDay(e, viewDate)),
        e => e.start
    );

    const chunkedEvents = React.useMemo(() => sortBy(chunkAndArrangeEvents(ordered), e => e.key), [ordered]);

    const onMouseDown = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            if (event.buttons % 2 !== 1) return;

            if (event.target instanceof HTMLElement) {
                const touchTarget = event.target.getAttribute("data-drag-handle");
                if (touchTarget === "upper" || touchTarget === "lower") {
                    event.preventDefault();
                    event.stopPropagation();
                    setDragging(touchTarget);
                    setDragStart([event.pageX, event.pageY]);
                    setDraggingSelection(selection);
                }
            }
        },
        [selection]
    );

    const onMoveCapture = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            if (dragging === undefined || dragStart === undefined || draggingSelection === undefined) return;
            event.preventDefault();
            event.stopPropagation();

            const deltaY = event.pageY - dragStart[1];

            const mins = deltaY * 1.2;

            const newSelection = { ...draggingSelection };
            if (dragging === "upper") {
                newSelection.start = new Date(
                    new Date(newSelection.start).setUTCMinutes(newSelection.start.getUTCMinutes() + mins)
                );
            } else {
                newSelection.end = new Date(
                    new Date(newSelection.end).setUTCMinutes(newSelection.end.getUTCMinutes() + mins)
                );
            }

            onSelectionChange?.(newSelection);
        },
        [dragging, dragStart, onSelectionChange, draggingSelection]
    );

    const onTouchDown = React.useCallback(
        (event: React.TouchEvent<HTMLDivElement>) => {
            if (event.target instanceof HTMLElement) {
                const touchTarget = event.target.getAttribute("data-drag-handle");
                if (touchTarget === "upper" || touchTarget === "lower") {
                    event.preventDefault();
                    event.stopPropagation();
                    setDragging(touchTarget);
                    setDragStart([event.touches[0].pageX, event.touches[0].pageY]);
                    setDraggingSelection(selection);
                }
            }
        },
        [selection]
    );

    const onTouchMoveCapture = React.useCallback(
        (event: TouchEvent) => {
            if (dragging === undefined || dragStart === undefined || draggingSelection === undefined) return;
            event.preventDefault();
            event.stopPropagation();

            const deltaY = event.touches[0].pageY - dragStart[1];

            const mins = deltaY * 1.2;

            const newSelection = { ...draggingSelection };
            if (dragging === "upper") {
                newSelection.start = new Date(
                    new Date(newSelection.start).setUTCMinutes(newSelection.start.getUTCMinutes() + mins)
                );
            } else {
                newSelection.end = new Date(
                    new Date(newSelection.end).setUTCMinutes(newSelection.end.getUTCMinutes() + mins)
                );
            }

            onSelectionChange?.(newSelection);
        },
        [dragging, dragStart, onSelectionChange, draggingSelection]
    );

    useEventListener("touchmove", onTouchMoveCapture, elRef.current, false);

    const onTouchEnd = React.useCallback(
        (event: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            if (dragging) {
                setDragging(undefined);
                setDragStart(undefined);
                setDraggingSelection(undefined);
                event.preventDefault();
                event.stopPropagation();
            }
        },
        [dragging]
    );

    const onCellClick = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            const el = event.currentTarget;
            if (dragging) {
                return;
            }

            const hour = el.getAttribute("data-hour");

            if (hour === null) return;

            const h = Number.parseInt(hour);

            const start = new Date(viewDate);
            start.setUTCHours(h, 0, 0, 0);
            const end = new Date(viewDate);
            end.setUTCHours(h + 1, 0, 0, 0);
            if (selection !== undefined) {
                const newSelection = {
                    ...selection,
                    start,
                    end,
                };
                onSelectionChange?.(newSelection);
            } else {
                onSelectionChange?.({
                    start,
                    end,
                    name: "",
                    description: "",
                    itemID: "selection",
                });
            }
        },
        [dragging, viewDate, onSelectionChange, selection]
    );

    React.useEffect(() => {
        const cb = window.setTimeout(() => {
            const scroller = scrollerRef.current;
            if (scroller === undefined || scroller === null) return;

            if (selection === undefined || !eventIsDuringDay(selection, getNowTimeZoneAgnostic())) {
                const scrollAmount = new Date().getHours() * 50;
                scroller.scrollTop = scrollAmount;
            } else {
                const scrollAmount = selection.start.getUTCHours() * 50;
                scroller.scrollTop = scrollAmount;
            }
        }, 150);

        return () => window.clearTimeout(cb);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <CalendarScheduleStyle className={className}>
            <label className="cal-date-header" onClick={onHeaderClick}>
                {header}
                <input
                    type="date"
                    onChange={e => {
                        const date = e.target.valueAsDate;
                        if (date === null) return;

                        onDateClick?.(date);
                    }}
                />
            </label>
            <div className="cal-week-picker" role="aside">
                <HorizontalCalendar selected={viewDate} onSelectionChange={onDateClick} canSwipe={true} mode="week" />
            </div>
            <div className="cal-scroller" ref={scrollerRef}>
                <div
                    className={classNames(dragging !== undefined && "dragging")}
                    ref={elRef}
                    onMouseDownCapture={onMouseDown}
                    onMouseMoveCapture={onMoveCapture}
                    onMouseUpCapture={onTouchEnd}
                    onMouseLeave={onTouchEnd}
                    onTouchStartCapture={onTouchDown}
                    onTouchEndCapture={onTouchEnd}
                    onTouchCancel={onTouchEnd}>
                    {[...Array(25).keys()].map(hour => {
                        const hourDate = new Date(new Date(viewDate).setUTCHours(hour % 24, 0, 0, 0));
                        const hidden = isWithin15Minutes(selection, hourDate);
                        return (
                            <div key={hour} className="time-block" aria-hidden={true}>
                                <div className={classNames("time-block-label", hidden && "hidden")}>
                                    {formatShortTime(hourDate)}
                                </div>
                                <div className="time-block-area" data-hour={hour} onClick={onCellClick}></div>
                            </div>
                        );
                    })}
                    <div
                        role="region"
                        aria-label="calendar events"
                        className={classNames("event-container", onEventClick && "clickable")}>
                        {chunkedEvents.map(e => {
                            const labelledById = kebabCase(`cal-event-name-${e.name}-${formatShortTime(e.start)}`);
                            return (
                                <article
                                    onClick={() => onEventClick?.(e.original)}
                                    key={e.key}
                                    className={classNames("cal-event", e.selected && "selection")}
                                    style={getEventStyle(viewDate, e.start, e.end, e.numColumns, e.col)}
                                    role={onEventClick === undefined ? undefined : "button"}
                                    aria-label="calendar events"
                                    aria-labelledby={labelledById}>
                                    <div id={labelledById} className="title">
                                        {e.name}
                                    </div>
                                    {e.selected && (
                                        <>
                                            <div data-drag-handle="upper" className="top-drag-handle" />
                                            <div data-drag-handle="lower" className="bottom-drag-handle" />
                                            <div className="start-time time-block-label">
                                                {formatShortTime(e.start)}
                                            </div>
                                            <div className="end-time time-block-label">{formatShortTime(e.end)}</div>
                                        </>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                </div>
            </div>
        </CalendarScheduleStyle>
    );
}

export default CalendarSchedule;
